/**
 * dispatch-countdown.js — single source of truth for the same-day-dispatch message,
 * evaluated in NZ warehouse time (Pacific/Auckland) regardless of the shopper's
 * device timezone.
 *
 * Why: the "ships today" promise is tied to the NZ warehouse's 3pm dispatch cutoff.
 * The old code read the shopper's LOCAL clock (new Date().getHours()), so an AU
 * customer — whose clock runs ~2h behind NZ (3h in DST-shoulder weeks, 4-5h for
 * Perth) — hit the "3pm" cutoff hours after the real NZ cutoff and was told
 * "ships today" when the warehouse had already closed for the day.
 *
 * Fix: derive the cutoff from NZ wall-clock, and DISPLAY a countdown ("Order within
 * 2h 15m for same-day dispatch"). A duration is timezone-invariant, so NZ and AU
 * shoppers see the same correct number with no per-market copy.
 *
 * Consumed by assets/product-inventory.js (product pages) and the cart surfaces
 * (snippets/cart-drawer.liquid, sections/main-cart.liquid) via the [data-ships] /
 * [data-dispatch-countdown] / [data-dispatch-day] markup in
 * snippets/cart-dispatch-message.liquid. A wrapper carrying [data-dispatch-line] is
 * hidden whenever state().reliable is false (no NZ clock, or no dispatch day found).
 *
 * Debug: set window.__DISPATCH_NOW to an ISO string to pin a fake instant.
 */
window.DispatchCountdown = window.DispatchCountdown || (function () {
  var WEEKDAYS = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

  // Same-day dispatch cutoff hour (NZ, 24h). Theme setting surfaced as
  // window.DISPATCH_CUTOFF_HOUR; defaults to 15 (3pm).
  function cutoffHour() {
    var h = parseInt(window.DISPATCH_CUTOFF_HOUR, 10);
    return (isNaN(h) || h < 0 || h > 23) ? 15 : h;
  }
  var WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  function baseDate() {
    return (typeof window.__DISPATCH_NOW !== 'undefined') ? new Date(window.__DISPATCH_NOW) : new Date();
  }

  // NZ public-holiday closure dates (observed/Mondayised), set from the theme setting
  // as window.DISPATCH_HOLIDAYS ("YYYY-MM-DD" per line). Parsed once into a lookup.
  var _holidays = null;
  function holidays() {
    if (_holidays) return _holidays;
    _holidays = {};
    var raw = window.DISPATCH_HOLIDAYS;
    if (typeof raw === 'string') {
      var toks = raw.split(/[\s,]+/);
      for (var i = 0; i < toks.length; i++) {
        if (/^\d{4}-\d{2}-\d{2}$/.test(toks[i])) _holidays[toks[i]] = true;
      }
    }
    return _holidays;
  }
  function pad2(n) { return (n < 10 ? '0' : '') + n; }
  function isoOf(y, mo, d) { return y + '-' + pad2(mo) + '-' + pad2(d); }
  // A day the NZ warehouse dispatches: a weekday that isn't a holiday.
  function isDispatchDay(dow, iso) { return dow >= 1 && dow <= 5 && !holidays()[iso]; }

  // Current NZ wall-clock as { day: 0-6, secondsIntoDay, y, mo, d }, or null if Intl
  // timezone support is unavailable.
  function nzNow() {
    try {
      var parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Pacific/Auckland',
        weekday: 'short', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
      }).formatToParts(baseDate());
      var map = {};
      for (var i = 0; i < parts.length; i++) map[parts[i].type] = parts[i].value;
      var day = WEEKDAYS[map.weekday];
      var hour = parseInt(map.hour, 10) % 24; // some engines emit '24' at midnight
      var min = parseInt(map.minute, 10);
      var sec = parseInt(map.second, 10);
      var y = parseInt(map.year, 10), mo = parseInt(map.month, 10), d = parseInt(map.day, 10);
      if (day === undefined || isNaN(hour) || isNaN(min) || isNaN(sec) || isNaN(y) || isNaN(mo) || isNaN(d)) return null;
      return { day: day, secondsIntoDay: hour * 3600 + min * 60 + sec, y: y, mo: mo, d: d };
    } catch (e) {
      return null;
    }
  }

  // Fallback to shopper-local time (old behaviour) only if Intl TZ isn't supported.
  function localNow() {
    var d = baseDate();
    return {
      day: d.getDay(), secondsIntoDay: d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds(),
      y: d.getFullYear(), mo: d.getMonth() + 1, d: d.getDate()
    };
  }

  // secsToCutoff is the wall-clock delta from "now" to the cutoff hour today in NZ,
  // which equals real elapsed seconds — NZ DST switches at ~2-3am, never between
  // business hours and the (afternoon) cutoff, so no UTC-offset math is needed.
  // First dispatch day strictly after `afterOff` calendar days from the NZ date `n` (afterOff = 0 →
  // tomorrow onwards). Returns { off, date, dow } or null if none within 3 weeks. UTC date arithmetic
  // off the NZ calendar date — a date's weekday is timezone-independent, so this is safe.
  function nextDispatchDay(n, afterOff) {
    var base = Date.UTC(n.y, n.mo - 1, n.d);
    for (var off = afterOff + 1; off <= afterOff + 21; off++) {
      var cand = new Date(base + off * 86400000);
      var iso = isoOf(cand.getUTCFullYear(), cand.getUTCMonth() + 1, cand.getUTCDate());
      if (isDispatchDay(cand.getUTCDay(), iso)) return { off: off, date: cand, dow: cand.getUTCDay() };
    }
    return null;
  }

  // Human label for a day found by nextDispatchDay: "tomorrow", a weekday name within the week, or a
  // date like "Mon 5 Jan" further out (e.g. a Christmas shutdown), where a weekday name is ambiguous.
  // nameFor is the same without the "tomorrow" shortcut (always a weekday name or date).
  function labelFor(found) {
    if (found.off === 1) return 'tomorrow';
    return nameFor(found);
  }
  function nameFor(found) {
    if (found.off <= 6) return WEEKDAY_NAMES[found.dow];
    try {
      return new Intl.DateTimeFormat('en-NZ', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' }).format(found.date);
    } catch (e) {
      return WEEKDAY_NAMES[found.dow];
    }
  }

  function state() {
    var nz = nzNow();
    var n = nz || localNow();
    var secsToCutoff = cutoffHour() * 3600 - n.secondsIntoDay;
    var todayIso = isoOf(n.y, n.mo, n.d);
    var sameDay = isDispatchDay(n.day, todayIso) && secsToCutoff > 0;
    // tomorrowDispatch (2026-10-11): is the NEXT NZ calendar day a dispatch day (weekday, not a
    // holiday)? Used by product-inventory.js for the "Get it tomorrow, NZ-wide" headline, which
    // only shows while sameDay is true AND tomorrow dispatches/delivers (so Mon-Thu, and never
    // the day before a public holiday). Same UTC date arithmetic as dayLabel below.
    var tmr = new Date(Date.UTC(n.y, n.mo - 1, n.d) + 86400000);
    var tomorrowDispatch = isDispatchDay(tmr.getUTCDay(), isoOf(tmr.getUTCFullYear(), tmr.getUTCMonth() + 1, tmr.getUTCDate()));

    // Urgency: within the last N minutes before the cutoff. Threshold is a theme
    // setting surfaced as window.DISPATCH_URGENCY_MIN (default 30).
    var urgencyMin = parseInt(window.DISPATCH_URGENCY_MIN, 10);
    if (isNaN(urgencyMin) || urgencyMin < 0) urgencyMin = 30;
    var urgent = sameDay && secsToCutoff <= urgencyMin * 60;

    // dayLabel: for the non-same-day case, the human label for the NEXT dispatch day,
    // skipping weekends AND holidays. Computed via UTC date arithmetic off the NZ
    // calendar date — a date's weekday is timezone-independent, so this is safe.
    var dayLabel = 'today';
    // dispatchOff: calendar days from today to the dispatch day (0 = ships today, -1 = none found).
    // dispatchName: the dispatch day as a weekday name / date, never "tomorrow" (for weekend copy
    // like "ships Monday"). todayDispatch: is today itself a working day for the warehouse?
    var dispatchOff = 0;
    var dispatchName = 'today';
    var todayDispatch = isDispatchDay(n.day, todayIso);
    if (!sameDay) {
      var found = nextDispatchDay(n, 0);
      dayLabel = found ? labelFor(found) : 'soon';
      dispatchName = found ? nameFor(found) : 'soon';
      dispatchOff = found ? found.off : -1;
    }

    // Delivery day (2026-10-16): the next dispatch day AFTER the dispatch day, i.e. the courier's
    // next working day — overnight couriers don't deliver on weekends or public holidays. Drives the
    // Friday / weekend / holiday-eve sub-line on the NZ overnight product line ("ships today,
    // delivered Monday"). deliveryOvernight is true when that day is the calendar day after dispatch
    // (the normal Mon-Thu case), so consumers can keep their existing copy then.
    // deliveryLabel2: one working day later again, for the South Island leg of the rod line
    // ("delivered Monday (South Island Tuesday)").
    var deliveryLabel = 'soon';
    var deliveryLabel2 = 'soon';
    var deliveryOvernight = false;
    if (dispatchOff >= 0) {
      var del = nextDispatchDay(n, dispatchOff);
      if (del) {
        deliveryLabel = labelFor(del);
        deliveryOvernight = del.off === dispatchOff + 1;
        var del2 = nextDispatchDay(n, del.off);
        if (del2) deliveryLabel2 = nameFor(del2);
      }
    }

    // reliable (2026-10-12): true only when the NZ wall-clock was available (Intl time zones)
    // AND, for the non-same-day case, a real next dispatch day was found. Strict consumers
    // ([data-dispatch-line], the cart page box) hide rather than show a guess when false.
    var reliable = !!nz && (sameDay || dayLabel !== 'soon');

    return { sameDay: sameDay, secsToCutoff: secsToCutoff, urgent: urgent, dayLabel: dayLabel, tomorrowDispatch: tomorrowDispatch, reliable: reliable, todayDispatch: todayDispatch, dispatchName: dispatchName, deliveryLabel: deliveryLabel, deliveryLabel2: deliveryLabel2, deliveryOvernight: deliveryOvernight };
  }

  // Readable spaced units: "54 min", "1 hr 5 min", "2 hr". Rounds minutes up so it
  // never reads "0 min" while the cutoff hasn't passed.
  function format(secs) {
    var total = Math.max(1, Math.ceil(secs / 60));
    var h = Math.floor(total / 60);
    var m = total % 60;
    if (h > 0) return m > 0 ? (h + ' hr ' + m + ' min') : (h + ' hr');
    return m + ' min';
  }

  // Cart markup: show the [data-ships] span matching the current key, hide the
  // rest, and fill any [data-dispatch-countdown] slot with the live remaining time.
  function apply(root) {
    var scope = root || document;
    var st = state();
    // IMPORTANT: cart callers run apply() from a MutationObserver on the cart
    // subtree. Writing to the DOM unconditionally (esp. textContent, a childList
    // mutation) would re-trigger that observer and loop forever, freezing the page.
    // So only touch the DOM when a value actually changes.
    scope.querySelectorAll('[data-ships]').forEach(function (el) {
      var want = st.sameDay ? 'today' : 'later';
      var disp = (el.dataset.ships === want) ? 'inline' : 'none';
      if (el.style.display !== disp) el.style.display = disp;
    });
    scope.querySelectorAll('[data-dispatch-countdown]').forEach(function (el) {
      var txt = format(st.secsToCutoff);
      if (el.textContent !== txt) el.textContent = txt;
      var urgent = !!st.urgent;
      if (el.classList.contains('dispatch-badge--urgent') !== urgent) {
        el.classList.toggle('dispatch-badge--urgent', urgent);
      }
    });
    scope.querySelectorAll('[data-dispatch-day]').forEach(function (el) {
      if (el.textContent !== st.dayLabel) el.textContent = st.dayLabel;
    });
    // Strict lines: hidden unless the state is reliable (see state()).
    scope.querySelectorAll('[data-dispatch-line]').forEach(function (el) {
      var hide = !st.reliable;
      if (el.hidden !== hide) el.hidden = hide;
    });
  }

  // One shared ticker for the whole page. Minute callbacks (onTick) drive the
  // dispatch message; second callbacks (onSecond) drive live per-second UIs like
  // the sale-ends countdown. The interval runs at 1s ONLY while a second-callback
  // is registered, otherwise at 60s — so pages without a live countdown keep the
  // original cheap minute cadence, and there is never more than one interval.
  var minuteCbs = [];
  var secondCbs = [];
  var timer = null;
  var timerMs = 0;
  var secTick = 0;
  function fireList(list) { for (var i = 0; i < list.length; i++) { try { list[i](); } catch (e) {} } }
  function schedule() {
    var wantMs = secondCbs.length ? 1000 : 60000;
    if (timer && timerMs === wantMs) return;
    if (timer) { clearInterval(timer); timer = null; }
    timerMs = wantMs;
    secTick = 0;
    timer = setInterval(function () {
      if (timerMs === 1000) {
        fireList(secondCbs);
        if (++secTick >= 60) { secTick = 0; fireList(minuteCbs); }
      } else {
        fireList(minuteCbs);
      }
    }, timerMs);
  }
  // Minute cadence: fires immediately on registration and ~every 60s thereafter.
  function onTick(cb) {
    minuteCbs.push(cb);
    try { cb(); } catch (e) {}
    schedule();
    return function off() {
      var i = minuteCbs.indexOf(cb);
      if (i !== -1) { minuteCbs.splice(i, 1); schedule(); }
    };
  }
  // Second cadence: fires immediately and every 1s. Returns an off() so callers
  // can stop ticking (e.g. when the countdown leaves the urgent tier or scrolls
  // off-screen), which lets the shared interval drop back to 60s.
  function onSecond(cb) {
    secondCbs.push(cb);
    try { cb(); } catch (e) {}
    schedule();
    return function off() {
      var i = secondCbs.indexOf(cb);
      if (i !== -1) { secondCbs.splice(i, 1); schedule(); }
    };
  }

  return { state: state, format: format, apply: apply, onTick: onTick, onSecond: onSecond };
})();
